import { useCallback, useEffect, useRef, useState } from 'react';
import { formatBackendError } from '../utils/formatError';

// <purpose>THE overlay-editing engine behind the "Texte & Vokabular" tab. Both sections — the
// reply-prose editor (prompt_overlay) and the vocabulary editor (vocabulary_overrides) — sit on
// the same backend shape: a catalog of rows whose EFFECTIVE value comes from a layered overlay,
// an optimistic save guarded by a baseRevision, a 409 on a lost update, and a reset that drops
// the override instead of restoring anything. This hook owns the five subtle rules that shape has
// (below); the sections own only their rendering and their endpoints.</purpose>
// <dependencies>formatBackendError (utils/formatError — the BE's German ErrorDe passes through
// verbatim); the caller's own fetch/save/reset functions, which it supplies.</dependencies>
// <constraints>The five rules, stated once here rather than re-derived per section:
// 1. RELOAD IS NOT RESEED. load() runs after every save/reset/rollback AND on 409, so a naive
//    reseed would clobber (a) every OTHER row's in-progress edit and (b) the user's OWN rejected
//    draft on 409, which is exactly the text they need to compare and re-save. A row's draft is
//    DIRTY iff it differs from THAT row's baseline as of the PREVIOUS load; dirty drafts survive
//    verbatim, clean ones are reseeded from the fresh server value. Rows/badges/revisions always
//    refresh regardless of dirtiness.
// 2. FROZEN baseRevision. save() must NOT send the freshest latestRevision: saving row B refreshes
//    EVERY row's revision, including dirty row A's. A's later save would then pass the very
//    conflict check the 409 exists to enforce. So the revision in effect the moment a draft FIRST
//    diverges is captured per row and is what save() sends. It clears on successful save, on local
//    draft-discard, and on reset; it is rebased ONLY on the 409 path, where the stale-conflict
//    notice makes the rebase user-acknowledged rather than silent.
//    A row that carries a SECOND field outside this engine (part 4b's letters, held in the
//    caller's own state) must freeze on THAT field's first dirty transition too, and must not
//    release until BOTH fields are clean — otherwise an edit that touches only the second field
//    sails past this engine's own freeze/release logic entirely, and a reload racing in from
//    another row's save silently rebases it onto a revision nobody diverged from (measured: fix
//    round 1, review of part 4b's letters band — a letters-only edit had no freeze at all).
//    `freezeIfUnfrozen` is that field's entry point for the dirty transition;
//    `setDraftValue`'s own `keepFrozenIfOtherDirty` callback is its veto on the release.
// 3. A REFETCH IS NOT A FIRST LOAD. load() runs on mount AND after every write, so `loading`
//    alone cannot gate a skeleton: doing so unmounts the whole list on each save (the card's
//    height changes twice) and, on the 409 path, unmounts the very draft the notice asks the
//    admin to compare. `initialLoading` is the gate; rows, once mounted, stay mounted.
// 4. TWO FEEDBACK SLOTS. `error` is a failure to retry; `notice` is informational ("we reloaded
//    for you"). Separate slots so a stale conflict notice and a real error never stomp each other.
//    Per-ROW failures go to fieldErrors, keyed by row, so one card's error never bleeds onto
//    another's — and any keystroke on that row discards its stale error.
// 5. The BE owns revision numbering (rollback/reset make it non-linear), so the client can never
//    compute the new state itself. Every write is followed by a refetch, happy path and 409 alike.
// </constraints>

/** The three fields this engine needs from a row, whatever else the row carries. */
export interface RevisionedRow {
  /** Stable per-row identity — the draft/baseline/freeze/error maps are all keyed by it. */
  key: string;
  /** The server's current effective value: the reseed source AND the dirty-comparison baseline. */
  effectiveText: string;
  /** The revision a save must base against when this row is clean. */
  latestRevision: number;
}

export interface UseRevisionedDrafts<TRow> {
  rows: TRow[];
  drafts: Record<string, string>;
  /** A fetch is in flight — the FIRST one or any write-triggered refetch. See `initialLoading`. */
  loading: boolean;
  /**
   * THE ONLY correct gate for a skeleton, and the reason it is exposed rather than left to each
   * consumer to re-derive: `loading` is also true during every write-triggered refetch, so gating
   * rows on it unmounts the whole list mid-write — the card blanks to a fixed-height placeholder
   * and back on every save, and on the 409 path it unmounts the rejected draft at exactly the
   * moment the notice asks the admin to compare it. A skeleton stands in for content that has
   * never been on screen; once rows exist they stay, and a refresh shows an inline busy hint
   * inside pre-allocated space instead.
   */
  initialLoading: boolean;
  error: string;
  notice: string;
  fieldErrors: Record<string, string>;
  setNotice: (v: string) => void;
  /** Refetch + merge per rule 1. Returns the fresh rows so a caller can rebase off them. */
  load: () => Promise<TRow[]>;
  /**
   * THE single writer for draft text — the freeze transitions of rule 2 live here.
   * `keepFrozenIfOtherDirty` is part 4b's escape hatch for a row that carries a SECOND
   * independently-dirtyable field outside this engine (the vocabulary letters band): when this
   * field's OWN transition would otherwise release the freeze (dirty -> clean), the caller can
   * veto that release by returning true here — the freeze must survive until BOTH fields are
   * clean, never just this one. Omitted (every caller with a single field) behaves exactly as
   * before: dirty -> clean always releases.
   */
  setDraftValue: (row: TRow, value: string, keepFrozenIfOtherDirty?: () => boolean) => void;
  /** The baseRevision a save must send for this row (rule 2). */
  baseRevisionFor: (row: TRow) => number;
  /**
   * Freezes this row's CURRENT latestRevision if it is not already frozen — a no-op when a freeze
   * is already in effect (whichever field dirtied first keeps its captured revision). The entry
   * point for a second field that dirties independently of the draft this engine owns (part 4b's
   * letters): call it on that field's OWN clean -> dirty transition, mirroring what
   * `setDraftValue` already does for the field this engine owns.
   */
  freezeIfUnfrozen: (row: TRow) => void;
  /** After a successful write: the draft is clean again, so the freeze is dropped. */
  clearFreeze: (key: string) => void;
  /** The 409 path's user-acknowledged rebase (rule 2's single exception). */
  rebaseFreeze: (freshRows: TRow[], key: string) => void;
  setFieldError: (key: string, message: string) => void;
  clearFieldError: (key: string) => void;
  /** True when this row's draft differs from the server's current effective value. */
  isDirty: (row: TRow) => boolean;
}

export function useRevisionedDrafts<TRow>(
  fetchRows: () => Promise<TRow[]>,
  project: (row: TRow) => RevisionedRow,
  loadErrorContext: string,
): UseRevisionedDrafts<TRow> {
  const [rows, setRows] = useState<TRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Refs, not state: the baseline must still reflect the PREVIOUS load when the new one lands, and
  // updating either of these must never itself trigger a render.
  const baselineRef = useRef<Record<string, string>>({});
  const frozenRevisionRef = useRef<Record<string, number>>({});

  // The caller's fetch/project are re-created on every render in practice (inline arrows at the
  // call site are the readable shape); keeping them in refs lets load() stay referentially stable,
  // so the mount effect below runs exactly once instead of refetching on every render.
  const fetchRef = useRef(fetchRows);
  fetchRef.current = fetchRows;
  const projectRef = useRef(project);
  projectRef.current = project;
  const contextRef = useRef(loadErrorContext);
  contextRef.current = loadErrorContext;

  const load = useCallback(async (): Promise<TRow[]> => {
    setLoading(true); setError('');
    try {
      const fresh = await fetchRef.current();
      const prevBaseline = baselineRef.current;
      setDrafts(prevDrafts => {
        const next: Record<string, string> = {};
        for (const row of fresh) {
          const p = projectRef.current(row);
          const hadBaseline = Object.prototype.hasOwnProperty.call(prevBaseline, p.key);
          const draftIsDirty = hadBaseline && prevDrafts[p.key] !== prevBaseline[p.key];
          next[p.key] = draftIsDirty ? prevDrafts[p.key] : p.effectiveText;
        }
        return next;
      });
      setRows(fresh);
      baselineRef.current = Object.fromEntries(
        fresh.map(row => { const p = projectRef.current(row); return [p.key, p.effectiveText]; }));
      return fresh;
    } catch (e) {
      setError(formatBackendError(e, contextRef.current));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearFieldError = useCallback((key: string) => setFieldErrors(fe => {
    if (!(key in fe)) return fe;
    const next = { ...fe };
    delete next[key];
    return next;
  }), []);

  const setFieldError = useCallback(
    (key: string, message: string) => setFieldErrors(fe => ({ ...fe, [key]: message })), []);

  const setDraftValue = (row: TRow, value: string, keepFrozenIfOtherDirty?: () => boolean) => {
    const p = projectRef.current(row);
    // Any keystroke on THIS row discards a stale error from a previous failed attempt (rule 3).
    clearFieldError(p.key);
    setDrafts(d => {
      const previous = d[p.key] ?? '';
      const wasDirty = previous !== p.effectiveText;
      const isNowDirty = value !== p.effectiveText;
      if (!wasDirty && isNowDirty) {
        // The freeze belongs to the FIRST field to diverge, whichever it is — never overwritten
        // by a second field's later transition (see `freezeIfUnfrozen`), so guard here too.
        if (!(p.key in frozenRevisionRef.current)) frozenRevisionRef.current[p.key] = p.latestRevision;
      } else if (wasDirty && !isNowDirty) {
        // Release only when nothing else on this row is still dirty — a caller with a second,
        // externally-held field (part 4b's letters) vetoes the release while that field diverges.
        if (!keepFrozenIfOtherDirty?.()) delete frozenRevisionRef.current[p.key];
      }
      return { ...d, [p.key]: value };
    });
  };

  const baseRevisionFor = (row: TRow) => {
    const p = projectRef.current(row);
    return frozenRevisionRef.current[p.key] ?? p.latestRevision;
  };

  const freezeIfUnfrozen = (row: TRow) => {
    const p = projectRef.current(row);
    if (!(p.key in frozenRevisionRef.current)) frozenRevisionRef.current[p.key] = p.latestRevision;
  };

  const clearFreeze = useCallback((key: string) => { delete frozenRevisionRef.current[key]; }, []);

  const rebaseFreeze = useCallback((freshRows: TRow[], key: string) => {
    const fresh = freshRows.find(r => projectRef.current(r).key === key);
    if (fresh) frozenRevisionRef.current[key] = projectRef.current(fresh).latestRevision;
  }, []);

  const isDirty = (row: TRow) => {
    const p = projectRef.current(row);
    return (drafts[p.key] ?? '') !== p.effectiveText;
  };

  return {
    rows, drafts, loading, initialLoading: loading && rows.length === 0, error, notice, fieldErrors,
    setNotice, load, setDraftValue, baseRevisionFor, freezeIfUnfrozen, clearFreeze, rebaseFreeze,
    setFieldError, clearFieldError, isDirty,
  };
}
